import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Activity, ArrowRight, ArrowUp, Award, Bell, Briefcase, Building2, Calendar, CheckCircle2, ChevronDown, ChevronLeft,
  ChevronRight, ClipboardCheck, Clock, Download, FileCheck2, FileText, Filter, Gauge, Globe, LayoutDashboard, Layers,
  Lightbulb, Mail, MapPin, Menu, MessageCircle, Moon, Phone, Quote, Search, Send, Server, Settings,
  ShieldCheck, Sparkles, Star, Sun, Target, ThumbsUp, TrendingUp, Trash2, Trophy, Upload, UserRound,
  Users, Wrench, X, Zap, LogOut, Database, RefreshCw, LockKeyhole, AlertTriangle
} from "lucide-react";
import {
  cases, certificates, navItems, news, services,
  stats, teamMembers, partners, faqItems, testimonials, serviceProcess, industrySolutions,
  timeline, careers, downloads, aboutContent,
} from "./data";
import { useScrollAnimation, useAnimatedCounter, useStaggerChildren, createRipple, useGlowPulse, readAnimSettings, saveAnimSettings } from "./hooks/useScrollAnimation";
import { api, downloadAdminBackup } from "./api.js";

const serviceIcons = [ClipboardCheck, ShieldCheck, Wrench, TrendingUp];
const sourceColors = ["#1f72f3", "#27b875", "#ff8a1f", "#8a63f6", "#10b8bd"];
const SITE_SETTINGS_STORAGE_KEY = "hnzw_site_settings_v1";
const DARK_MODE_KEY = "hnzw_dark_mode";
const SMART_ENERGY_URL = "https://dzny.site/";

const DEFAULT_SITE_SETTINGS = {
  siteName: "华能智维电力", brandEnglish: "HUANENG ZHIWEI ELECTRIC POWER",
  hotline: "400-886-1996", email: "service@hnzwdl.com",
  address: "江苏省南京市江宁区启航路 99 号", icpRecord: "苏ICP备2024001234号-1", verificationVisits: 5, publicAccess: true,
};

/* ─── Helpers ─── */
function normalizeSiteSettings(s) {
  return {
    ...Object.fromEntries(Object.entries(DEFAULT_SITE_SETTINGS).filter(([key]) => key !== "publicAccess").map(([k, f]) => [k, String(s?.[k] ?? f).trim() || f])),
    verificationVisits: Math.min(20, Math.max(1, Number(s?.verificationVisits) || 5)),
    publicAccess: s?.publicAccess !== false,
  };
}
function readStoredSiteSettings() {
  try { const r = window.localStorage.getItem(SITE_SETTINGS_STORAGE_KEY); return r ? normalizeSiteSettings(JSON.parse(r)) : DEFAULT_SITE_SETTINGS; } catch { return DEFAULT_SITE_SETTINGS; }
}
function readDarkMode() {
  try { return localStorage.getItem(DARK_MODE_KEY) === "true"; } catch { return false; }
}
function saveDarkMode(v) {
  try { localStorage.setItem(DARK_MODE_KEY, String(v)); } catch { /* */ }
}
function setHash(h) { window.location.hash = h; }
function scrollToSection(id) { document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); }

const anchorMap = { 首页: "home", 关于我们: "about", 服务项目: "services", 工程案例: "cases",
  新闻资讯: "news", 行业方案: "solutions", 人才招聘: "careers", 下载中心: "downloads", 联系我们: "contact" };

/* ─── Router ─── */
function parseRoute() {
  const h = window.location.hash.replace("#", "") || "/";
  if (h === "/" || h === "/home") return { page: "home" };
  if (h === "/console") return { page: "console" };
  const m = h.match(/^\/(service|case|news)\/(.+)$/);
  if (m) return { page: m[1], id: m[2] };
  return { page: "home" };
}
function useRoute() {
  const [route, setRoute] = useState(parseRoute);
  useEffect(() => {
    const cb = () => { setRoute(parseRoute()); window.scrollTo({ top: 0, behavior: "smooth" }); };
    window.addEventListener("hashchange", cb);
    return () => window.removeEventListener("hashchange", cb);
  }, []);
  return route;
}

/* ─── Toast ─── */
function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2000); return () => clearTimeout(t); }, [onDone]);
  return <div className="toast-bar">{message}</div>;
}

/* ─── Back to Top ─── */
function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const cb = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", cb, { passive: true });
    return () => window.removeEventListener("scroll", cb);
  }, []);
  if (!show) return null;
  return (
    <button className="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="回到顶部">
      <ArrowUp size={22} />
    </button>
  );
}

/* ─── Search Modal ─── */
function SearchModal({ onClose }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { const cb = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", cb); return () => window.removeEventListener("keydown", cb); }, [onClose]);

  const allItems = useMemo(() => {
    const r = [];
    services.forEach(s => r.push({ type: "服务", title: s.title, desc: s.summary, id: s.id }));
    cases.forEach(c => r.push({ type: "案例", title: c.title, desc: c.result, id: c.id }));
    news.forEach(n => r.push({ type: "新闻", title: n.title, desc: n.summary, id: n.id }));
    certificates.forEach(c => r.push({ type: "资质", title: c, desc: "", id: "" }));
    return r;
  }, []);

  const filtered = q.trim() ? allItems.filter(it => it.title.includes(q) || it.desc.includes(q) || it.type.includes(q)) : [];

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-modal" onClick={e => e.stopPropagation()}>
        <div className="search-input-wrap">
          <Search size={20} /><input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="搜索服务、案例、新闻、资质..." />
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {q && <div className="search-results">
          {filtered.length === 0 ? <p className="search-empty">未找到相关内容</p> : filtered.map((it, i) => (
            <button key={i} className="search-result-item" onClick={() => {
              onClose();
              if (it.type === "服务" && it.id) setHash(`/service/${it.id}`);
              else if (it.type === "案例" && it.id) setHash(`/case/${it.id}`);
              else if (it.type === "新闻" && it.id) setHash(`/news/${it.id}`);
              else scrollToSection("about");
            }}>
              <span className="search-result-type">{it.type}</span>
              <strong>{it.title}</strong>
              <p>{it.desc}</p>
            </button>
          ))}
        </div>}
      </div>
    </div>
  );
}

/* ─── Progress Bar ─── */
function ProgressBar() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const cb = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setW(total > 0 ? (h.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", cb, { passive: true });
    return () => window.removeEventListener("scroll", cb);
  }, []);
  return <div className="progress-bar" style={{ width: `${w}%` }} />;
}

/* ─── Hero Slideshow ─── */
const heroSlides = [
  { image: "/assets/hero-substation.png", label: "变电站运维" },
  { image: "/assets/solar-storage.png", label: "光伏储能建设" },
  { image: "/assets/distribution-room.png", label: "配电系统升级" },
  { image: "/assets/switchgear-service.png", label: "设备检修安装" },
  { image: "/assets/company-campus.png", label: "电力检测年检" },
];

function HeroSlideshow({ current, setCurrent }) {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setCurrent(c => (c + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [paused, setCurrent]);

  return (
    <div
      className="hero-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-hidden="true"
    >
      {heroSlides.map((slide, i) => (
        <div key={i} className={`hero-slide ${i === current ? "active" : ""}`}>
          <img src={slide.image} alt={slide.label} />
        </div>
      ))}
    </div>
  );
}

/* ─── Notification Panel ─── */
const DEFAULT_NOTIFICATIONS = [
  { id: 1, text: "新询盘：江苏速达工业园提交了电力检测需求", time: "10分钟前", read: false },
  { id: 2, text: "系统提醒：苏州工业园区变电站改造项目即将到期", time: "30分钟前", read: false },
  { id: 3, text: "内容更新：新闻资讯「华能智维完成年度配电检测」已发布", time: "1小时前", read: false },
  { id: 4, text: "新询盘：山东中能能源有限公司提交了变电运维咨询", time: "2小时前", read: false },
  { id: 5, text: "系统提醒：南京高新区光伏储能项目进度更新至45%", time: "3小时前", read: false },
  { id: 6, text: "账号安全：管理员 admin 于 09:15 登录控制台", time: "4小时前", read: false },
  { id: 7, text: "询盘转化：合肥高新产业园询盘已成功转化", time: "昨天 16:22", read: true },
  { id: 8, text: "内容审核：资质证书「承装（修、试）电力设施许可证」审核通过", time: "昨天 14:05", read: true },
];

function NotificationPanel({ notifications, onMarkAllRead, onClose }) {
  const u = notifications.filter(n => !n.read).length;
  return (
    <div className="notify-overlay" onClick={onClose}>
      <div className="notify-dropdown" onClick={e => e.stopPropagation()}>
        <div className="notify-header">
          <h3>消息通知</h3>
          {u > 0 && <button className="notify-read-all" onClick={onMarkAllRead}>全部已读 ({u})</button>}
        </div>
        <div className="notify-list">
          {notifications.map(n => (
            <div key={n.id} className={`notify-item${n.read ? "" : " unread"}`}>
              <div className="notify-dot" /><div className="notify-body"><p>{n.text}</p><time>{n.time}</time></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Edit Modal ─── */
function EditModal({ item, section, onSave, onCancel }) {
  const [title, setTitle] = useState(item.title); const [summary, setSummary] = useState(item.summary || "");
  const [image, setImage] = useState(item.image || ""); const [preview, setPreview] = useState(item.image || "");
  useEffect(() => { const cb = (e) => { if (e.key === "Escape") onCancel(); }; window.addEventListener("keydown", cb); return () => window.removeEventListener("keydown", cb); }, [onCancel]);
  const handleImg = (e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = ev => { setImage(ev.target.result); setPreview(ev.target.result); }; r.readAsDataURL(f); } };
  const build = () => ({ ...item, title: title.trim(), summary: summary.trim(), image: preview });
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>编辑{section}内容</h3><button className="modal-close" onClick={onCancel}><X size={20} /></button></div>
        <form onSubmit={e => { e.preventDefault(); if (!title.trim()) return; onSave(build(), false); }}>
          <label>标题<input value={title} onChange={e => setTitle(e.target.value)} autoFocus required /></label>
          <label>摘要<textarea value={summary} onChange={e => setSummary(e.target.value)} rows="4" placeholder="输入内容摘要" /></label>
          <label>图片</label>
          {preview ? <div className="image-preview"><img src={preview} alt="Preview" /><button type="button" className="image-remove" onClick={() => { setImage(""); setPreview(""); }}>移除图片</button></div>
            : <label className="upload-box"><Upload size={22} />上传封面图片<input type="file" accept="image/*" onChange={handleImg} /></label>}
          <div className="modal-actions">
            <button className="console-primary" type="submit">保存修改</button>
            <button className="btn-publish" type="button" onClick={e => { e.preventDefault(); if (!title.trim()) return; onSave(build(), true); }}>保存并发布</button>
            <button className="ghost-button" type="button" onClick={onCancel}>取消</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Brand ─── */
function Brand({ inverted = false, settings = DEFAULT_SITE_SETTINGS }) {
  return (
    <button className={`brand ${inverted ? "brand-inverted" : ""}`} onClick={() => setHash("/")}>
      <span className="brand-mark"><Zap size={24} /></span>
      <span><strong>{settings.siteName}</strong><em>{settings.brandEnglish}</em></span>
    </button>
  );
}

/* ─── PanelTitle ─── */
function PanelTitle({ title, action, onAction }) {
  return <div className="panel-title"><h2>{title}</h2>{action && <button onClick={onAction}>{action} <ChevronRight size={15} /></button>}</div>;
}

function TrendChart({ data }) {
  if (!data.length) return <div className="chart-empty"><Activity size={24} /><span>暂无可绘制的询盘趋势</span></div>;
  const width = 720; const height = 270; const left = 44; const right = 18; const top = 22; const bottom = 42;
  const maxValue = Math.max(1, ...data.flatMap(item => [item.total, item.valid]));
  const point = (value, index) => {
    const x = left + (index * (width - left - right)) / Math.max(1, data.length - 1);
    const y = top + (1 - value / maxValue) * (height - top - bottom);
    return [Number(x.toFixed(1)), Number(y.toFixed(1))];
  };
  const totalPoints = data.map((item, index) => point(item.total, index));
  const validPoints = data.map((item, index) => point(item.valid, index));
  return <div className="native-chart"><div className="chart-legend"><span><i className="total" />询盘总数</span><span><i className="valid" />已处理询盘</span></div><svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="询盘趋势图">{[0, .25, .5, .75, 1].map(value => { const y = top + value * (height - top - bottom); return <line key={value} x1={left} x2={width - right} y1={y} y2={y} className="chart-grid-line" />; })}<polyline points={totalPoints.map(item => item.join(",")).join(" ")} className="chart-line total" /><polyline points={validPoints.map(item => item.join(",")).join(" ")} className="chart-line valid" />{totalPoints.map(([x, y], index) => <circle key={`t-${data[index].day}`} cx={x} cy={y} r="4" className="chart-point total"><title>{data[index].day}：{data[index].total} 条</title></circle>)}{data.map((item, index) => { const [x] = point(item.total, index); return <text key={item.day} x={x} y={height - 13} textAnchor="middle" className="chart-axis-label">{item.day}</text>; })}</svg></div>;
}

function SourceDonut({ data }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cursor = 0;
  const segments = data.map((item, index) => {
    const start = cursor;
    cursor += total ? item.value / total * 360 : 0;
    return `${sourceColors[index % sourceColors.length]} ${start}deg ${cursor}deg`;
  });
  const background = total ? `conic-gradient(${segments.join(",")})` : "#dce3ed";
  return <div className="source-donut" style={{ background }} role="img" aria-label={`询盘来源分布，总计 ${total} 条`}><span><strong>{total}</strong>总计</span></div>;
}

/* ─── AnimatedStat ─── */
function AnimatedStat({ value, label, icon: Icon }) {
  const [ref, count] = useAnimatedCounter(parseInt(value) || 0);
  const suffix = value.includes("+") ? "+" : "";
  return <div className="stat-card" ref={ref}>{Icon && <Icon size={24} />}<strong>{count}{suffix}</strong><span>{label}</span></div>;
}

/* ─── ScrollSection ─── */
function ScrollSection({ children, className = "", delay = 0, ...rest }) {
  const [ref, animClass] = useScrollAnimation({ delay });
  return <div ref={ref} className={`${className} ${animClass}`.trim()} {...rest}>{children}</div>;
}

/* ===================================================================
   CORPORATE SITE (main site + sub-pages)
   =================================================================== */
function CorporateSite({ siteSettings, darkMode, toggleDarkMode, route, onSettingsSynced }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [consultation, setConsultation] = useState({ name: "", company: "", phone: "", service: "电力检测与年检" });
  const [submitted, setSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ loading: false, error: "", reference: "" });
  const [openFaq, setOpenFaq] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [heroCurrent, setHeroCurrent] = useState(0);
  const [caseFilter, setCaseFilter] = useState("全部");
  const [newsEmail, setNewsEmail] = useState("");
  const [newsSubscribed, setNewsSubscribed] = useState(false);
  const [newsSubscribeStatus, setNewsSubscribeStatus] = useState({ loading: false, error: "" });
  const [publicContent, setPublicContent] = useState(null);
  const staggerDelays = useStaggerChildren(4, 100);
  const glowClass = useGlowPulse();
  const animSettings = readAnimSettings();
  const isSubPage = ["service", "case", "news"].includes(route.page);

  // Mobile detection & collapse states
  const [isMobile, setIsMobile] = useState(window.innerWidth < 760);
  const [solutionsExpanded, setSolutionsExpanded] = useState(false);
  const [careersExpanded, setCareersExpanded] = useState(false);
  const [downloadsExpanded, setDownloadsExpanded] = useState(false);
  const [downloadToast, setDownloadToast] = useState(null);
  const [expandedSolutionCards, setExpandedSolutionCards] = useState(new Set());
  const [expandedCareerCards, setExpandedCareerCards] = useState(new Set());

  useEffect(() => {
    const cb = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", cb);
    return () => window.removeEventListener("resize", cb);
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([api.publicSettings(), api.publicContent()]).then(([settingsResult, contentResult]) => {
      if (!active) return;
      onSettingsSynced?.(settingsResult.settings);
      setPublicContent(contentResult.content);
    }).catch(() => { /* 入口安全检查已处理服务不可达状态 */ });
    return () => { active = false; };
  }, [onSettingsSynced]);

  useEffect(() => {
    try {
      if (!localStorage.getItem("hnzw_design_v2_migrated")) {
        const current = readAnimSettings();
        saveAnimSettings({ ...current, parallax: false, particle: false, tilt3D: false, shimmer: false, hoverScale: false, glowPulse: false, intensity: "subtle" });
        localStorage.setItem("hnzw_design_v2_migrated", "true");
      }
    } catch { /* storage may be unavailable */ }
  }, []);

  const handleDownload = (dl) => {
    setDownloadToast(`${dl.title} 尚未上传正式文件，请联系 ${siteSettings.hotline} 获取`);
    setTimeout(() => setDownloadToast(null), 3200);
  };

  const submitConsultation = async (event) => {
    event.preventDefault();
    setSubmitStatus({ loading: true, error: "", reference: "" });
    try {
      const result = await api.submitLead(consultation);
      setSubmitted(true);
      setSubmitStatus({ loading: false, error: "", reference: result.lead.id });
    } catch (error) {
      setSubmitStatus({ loading: false, error: error.message, reference: "" });
    }
  };

  const submitSubscription = async (event) => {
    event.preventDefault();
    setNewsSubscribeStatus({ loading: true, error: "" });
    try {
      await api.subscribe(newsEmail);
      setNewsSubscribed(true);
      setNewsEmail("");
      setNewsSubscribeStatus({ loading: false, error: "" });
    } catch (error) {
      setNewsSubscribeStatus({ loading: false, error: error.message });
    }
  };

  const serviceItems = publicContent?.["服务项目"]?.length ? publicContent["服务项目"] : services;
  const caseItems = publicContent?.["工程案例"]?.length ? publicContent["工程案例"] : cases;
  const newsItems = publicContent?.["新闻资讯"]?.length ? publicContent["新闻资讯"] : news;
  const certificateItems = publicContent?.["资质证书"]?.length ? publicContent["资质证书"].map(item => item.title) : certificates;

  // Per-card expand toggle for mobile
  const toggleSolutionCard = (id) => {
    setExpandedSolutionCards(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const toggleCareerCard = (id) => {
    setExpandedCareerCards(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  // Hero image error fallback
  const [teamImgErrors, setTeamImgErrors] = useState({});
  const handleTeamImgError = (name) => {
    setTeamImgErrors(prev => ({ ...prev, [name]: true }));
  };

  const teamAvatarColors = [
    { bg: "#1f72f3", fg: "#ffffff" },
    { bg: "#27b875", fg: "#ffffff" },
    { bg: "#7c3aed", fg: "#ffffff" },
    { bg: "#ff8a1f", fg: "#ffffff" },
    { bg: "#10b8bd", fg: "#ffffff" },
    { bg: "#e6a20d", fg: "#ffffff" },
  ];

  const getInitials = (name) => name.replace(/[^\u4e00-\u9fa5]/g, "").slice(0, 2) || name.charAt(0);

  useEffect(() => {
    const cb = () => setHeaderScrolled(window.scrollY > 10);
    window.addEventListener("scroll", cb, { passive: true });
    return () => window.removeEventListener("scroll", cb);
  }, []);

  const goAnchor = (item) => {
    if (item === "智慧能碳平台") { window.open(SMART_ENERGY_URL, "_blank", "noopener"); return; }
    if (item === "行业方案") { scrollToSection("solutions"); setMobileOpen(false); return; }
    if (item === "人才招聘") { scrollToSection("careers"); setMobileOpen(false); return; }
    if (item === "下载中心") { scrollToSection("downloads"); setMobileOpen(false); return; }
    if (anchorMap[item]) {
      if (isSubPage) { setHash("/"); setTimeout(() => scrollToSection(anchorMap[item]), 100); }
      else scrollToSection(anchorMap[item]);
    }
    setMobileOpen(false);
  };

  // Keyboard shortcut for search
  useEffect(() => {
    const cb = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", cb);
    return () => window.removeEventListener("keydown", cb);
  }, []);

  const header = (
    <>
      {animSettings.particle && <div className="particle-bg" aria-hidden="true" />}
      <div className="top-strip">
        <div className="container strip-inner">
          <span><Phone size={15} /> 全国服务热线：{siteSettings.hotline}</span>
          <span className="top-strip-email"><Mail size={15} /> {siteSettings.email}</span>
          <span className="top-strip-actions">
            <button className="top-icon-btn" onClick={() => setSearchOpen(true)} aria-label="搜索"><Search size={16} /></button>
            <button className="top-icon-btn" onClick={toggleDarkMode} aria-label="切换暗色模式">{darkMode ? <Sun size={16} /> : <Moon size={16} />}</button>
            <button onClick={() => window.open(SMART_ENERGY_URL, "_blank", "noopener")}>智慧能碳平台</button>
          </span>
        </div>
      </div>

      <header className={`site-header${headerScrolled ? " scrolled" : ""}`}>
        <div className="container header-inner">
          <Brand settings={siteSettings} />
          <div className="header-right">
            <button className="header-search-btn" onClick={() => setSearchOpen(true)} aria-label="搜索"><Search size={20} /></button>
            <button className="mobile-menu-button" aria-label="打开导航" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
          <nav className={mobileOpen ? "site-nav nav-open" : "site-nav"} aria-label="主导航">
            {navItems.map(item => (
              <button key={item} onClick={() => goAnchor(item)} className={item === "智慧能碳平台" ? "nav-special" : ""}>{item}</button>
            ))}
          </nav>
        </div>
      </header>
    </>
  );

  const footer = (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div>
          <Brand inverted settings={siteSettings} />
          <p>专注电力服务，保障安全运行，助力能源转型，创造持续价值。</p>
          <div className="footer-social">
            <span>关注我们</span>
            <div className="social-icons">
              <button aria-label="微信"><MessageCircle size={18} /></button>
              <button aria-label="微博"><Globe size={18} /></button>
              <button aria-label="电话"><Phone size={18} /></button>
            </div>
          </div>
        </div>
        <div>
          <h3>服务项目</h3>
          {services.map(s => <button key={s.title} onClick={() => { setHash(`/service/${s.id}`); }}>{s.title}</button>)}
        </div>
        <div>
          <h3>快捷链接</h3>
          <button onClick={() => setHash("/")}>首页</button>
          <button onClick={() => scrollToSection("about")}>关于我们</button>
          <button onClick={() => scrollToSection("cases")}>工程案例</button>
          <button onClick={() => scrollToSection("news")}>新闻资讯</button>
          <button onClick={() => scrollToSection("solutions")}>行业方案</button>
        </div>
        <div>
          <h3>联系我们</h3>
          <div className="footer-contact-info">
            <span><Phone size={16} /> {siteSettings.hotline}</span>
            <span><Mail size={16} /> {siteSettings.email}</span>
            <span><MapPin size={16} /> {siteSettings.address}</span>
          </div>
        </div>
      </div>
      <div className="container copyright">© 2026 {siteSettings.siteName} 版权所有　{siteSettings.icpRecord}</div>
    </footer>
  );

  /* ===== Sub-pages ===== */
  const shellClass = `site-shell ${darkMode ? "dark" : ""} ${animSettings.hoverScale ? "anim-hover-scale" : ""}`;
  if (route.page === "service") return (
    <div className={shellClass}>
      <ProgressBar />
      {header}
      <ServiceDetailPage serviceId={route.id} />
      {footer}
      <BackToTop />
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </div>
  );
  if (route.page === "case") return (
    <div className={shellClass}>
      <ProgressBar />
      {header}
      <CaseDetailPage caseId={route.id} />
      {footer}
      <BackToTop />
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </div>
  );
  if (route.page === "news") return (
    <div className={shellClass}>
      <ProgressBar />
      {header}
      <NewsDetailPage newsId={route.id} />
      {footer}
      <BackToTop />
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </div>
  );

  /* ===== Main Homepage ===== */
  return (
    <div className={shellClass}>
      <ProgressBar />
      {header}
      <main>
        {/* HERO */}
        <section id="home" className="hero-section">
          <HeroSlideshow current={heroCurrent} setCurrent={setHeroCurrent} />
          <div className="hero-dots">
            {heroSlides.map((_, i) => (
              <button key={i} className={`hero-dot ${i === heroCurrent ? "active" : ""}`} onClick={() => setHeroCurrent(i)} aria-label={`切换到第${i + 1}张`} />
            ))}
          </div>
          {animSettings.parallax && <div className="hero-parallax" aria-hidden="true" />}
          <div className="container hero-grid">
            <div className="hero-copy">
              <h1>专业电力服务，<br />保障<span className="highlight">稳定运行</span></h1>
              <p>聚焦电力检测年检、变电运维、设备检修安装、电力系统升级与新能源建设，为工业客户提供可靠的一站式工程服务。</p>
              <div className="hero-actions">
                <button className="primary-btn" onClick={(e) => { createRipple(e); scrollToSection("services"); }}>
                  了解服务 <ChevronRight size={18} />
                </button>
                <button className="secondary-btn" onClick={(e) => { createRipple(e); scrollToSection("contact"); }}>
                  在线咨询 <MessageCircle size={17} />
                </button>
              </div>
              <div className="hero-proof"><span><CheckCircle2 size={16} /> 标准化项目交付</span><span><ShieldCheck size={16} /> 全流程安全管控</span><span><Clock size={16} /> 7×24 服务响应</span></div>
            </div>
            <div className={`hero-panel ${glowClass}`} aria-label="服务保障"><div><span>服务承诺</span><strong>快速响应</strong><em>需求确认后安排技术顾问对接</em></div><div><span>交付方式</span><strong>全程留档</strong><em>方案、施工、验收节点可追溯</em></div></div>
          </div>
        </section>

        {/* SERVICES */}
        <ScrollSection id="services" className="service-entry section-pad">
          <div className="container">
            <div className="section-head" style={{ marginBottom: 38 }}>
              <div><span className="section-label">服务项目</span><h2>一站式电力工程服务，保障安全高效运行</h2></div>
            </div>
            <div className="service-grid">
              {serviceItems.slice(0, 4).map((svc, i) => {
                const Icon = serviceIcons[i] || Wrench;
                return (
                  <article className="service-item" key={svc.title}>
                    <div className="service-icon-wrap"><Icon size={28} /></div>
                    <h2>{svc.title}</h2>
                    <p>{svc.summary}</p>
                    {svc.metric && <div className="service-metric"><strong>{svc.metric}</strong><span>{svc.metricLabel}</span></div>}
                    <button onClick={() => svc.id && services.some(item => item.id === svc.id) ? setHash(`/service/${svc.id}`) : scrollToSection("contact")}>了解更多 <ChevronRight size={16} /></button>
                  </article>
                );
              })}
            </div>
          </div>
        </ScrollSection>

        {/* ABOUT */}
        <ScrollSection id="about" className="about-section section-pad" delay={50}>
          <div className="container about-grid">
            <div className="section-copy">
              <span className="section-label">关于我们</span>
              <h2>值得信赖的电力服务伙伴</h2>
              <p>华能智维电力是一家专注于电力工程服务的高新技术企业，业务覆盖电力检测年检、变电运维、设备检修与安装、电力系统升级、分布式光伏与储能建设等领域。</p>
              <p>我们以安全、专业、高效、创新为服务理念，依托资深技术团队与先进检测设备，为工业园区、工厂企业、公共机构及能源项目业主提供贯穿全生命周期的电力解决方案。</p>
              <div className="stats-row">{stats.map(([v, l]) => <AnimatedStat key={l} value={v} label={l} />)}</div>
              <button className="primary-btn compact" onClick={() => scrollToSection("contact")}>了解更多关于我们</button>
            </div>
            <div className="about-media">
              <img src="/assets/company-campus.png" alt="华能智维电力办公与工程基地" />
              <div className="thumb-row">
                <img src="/assets/switchgear-service.png" alt="工程师检修配电柜" />
                <img src="/assets/distribution-room.png" alt="现代化配电室" />
                <img src="/assets/solar-storage.png" alt="光伏储能项目" />
              </div>
            </div>
          </div>
        </ScrollSection>

        {/* SERVICE PROCESS */}
        <ScrollSection className="process-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 48, textAlign: "center", justifyContent: "center" }}>
              <div><span className="section-label">服务流程</span><h2>专业规范的服务流程</h2></div>
            </div>
            <div className="process-steps">
              {serviceProcess.map(s => (
                <div className="process-step" key={s.step}><div className="step-number">{s.step}</div><div className="step-line" /><h3>{s.title}</h3><p>{s.desc}</p></div>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* CASES */}
        <ScrollSection id="cases" className="cases-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head">
              <div><span className="section-label">工程案例</span><h2>为各行业客户提供高质量电力工程与运维服务</h2></div>
              <button className="outline-btn" onClick={() => scrollToSection("cases")}>查看全部案例</button>
            </div>
            <div className="case-filter-bar">
              {["全部", "变电运维", "光伏储能", "系统升级", "检修安装"].map(cat => (
                <button key={cat} className={caseFilter === cat ? "active" : ""} onClick={() => setCaseFilter(cat)}>{cat}</button>
              ))}
            </div>
            <div className="case-grid">
              {(caseFilter === "全部" ? caseItems : caseItems.filter(c => c.category === caseFilter)).map(c => (
                <article className="case-card tilt-card stagger-item" key={c.id} onClick={() => setHash(`/case/${c.id}`)} style={{ cursor: "pointer" }}>
                  {c.image && <div className="card-img-wrap"><img src={c.image} alt={c.title} /><div className="card-img-overlay" /></div>}
                  <div className="card-body">{c.category && <span className="card-tag">{c.category}</span>}<h3>{c.title}</h3><p>{c.result || c.summary}</p></div>
                </article>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* TESTIMONIALS */}
        <ScrollSection className="testimonial-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40, textAlign: "center", justifyContent: "center" }}>
              <div><span className="section-label">客户评价</span><h2>客户信赖，品质见证</h2></div>
            </div>
            <div className="testimonial-grid">
              {testimonials.map(t => (
                <div className="testimonial-card" key={t.name}><Quote size={28} className="testimonial-quote" /><p>{t.content}</p>
                  <div className="testimonial-stars">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={16} fill="#ff8a1f" color="#ff8a1f" />)}</div>
                  <div className="testimonial-author"><strong>{t.name}</strong><span>{t.role}</span></div>
                </div>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* CERTIFICATES */}
        <ScrollSection className="cert-section" delay={50}>
          <div className="container">
            <div className="section-head compact-head" style={{ marginBottom: 30 }}>
              <div><span className="section-label">资质与认证</span><h2>权威资质认证，专业能力保障</h2></div>
            </div>
            <div className="cert-grid">
              {certificateItems.map(c => (
                <div className="cert-card" key={c}><div className="cert-icon"><FileCheck2 size={24} /></div><strong>{c}</strong></div>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* TEAM */}
        <ScrollSection className="team-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40 }}>
              <div><span className="section-label">核心团队</span><h2>经验丰富的专业团队</h2></div>
            </div>
            <div className="team-grid">
              {teamMembers.map((m, i) => (
                <div className="team-card tilt-card" key={m.name}>
                  <div className="team-avatar">
                    {teamImgErrors[m.name] || !m.avatar ? (
                      <span className="team-avatar-initials" style={{ background: teamAvatarColors[i % teamAvatarColors.length].bg, color: teamAvatarColors[i % teamAvatarColors.length].fg }}>{getInitials(m.name)}</span>
                    ) : (
                      <img src={m.avatar} alt={m.name} onError={() => handleTeamImgError(m.name)} />
                    )}
                  </div>
                  <h3>{m.name}</h3>
                  <span className="team-role">{m.role}</span>
                  <p>{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* INDUSTRY SOLUTIONS */}
        <ScrollSection id="solutions" className="solutions-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40 }}>
              <div><span className="section-label">行业方案</span><h2>面向多行业的定制化电力解决方案</h2></div>
            </div>
            <div className={`solutions-grid ${isMobile && !solutionsExpanded ? "solutions-collapsed" : ""}`}>
              {industrySolutions.map(s => (
                <div className={`solution-card ${isMobile && expandedSolutionCards.has(s.id) ? "card-expanded" : ""}`} key={s.id}>
                  <div className="solution-icon" style={{ color: s.color, background: `${s.color}14` }}><Layers size={28} /></div>
                  <h3>{s.title}</h3>
                  <p className="solution-desc">{s.desc}</p>
                  <ul className="solution-points">
                    {s.points.map((p, i) => <li key={i}><CheckCircle2 size={14} color={s.color} />{p}</li>)}
                  </ul>
                  <div className="solution-cases">
                    {s.cases.map((c, i) => <span key={i} className="solution-case-tag">{c}</span>)}
                  </div>
                  {isMobile && (
                    <button className="card-expand-toggle" onClick={() => toggleSolutionCard(s.id)}>
                      {expandedSolutionCards.has(s.id) ? "收起" : "展开详情"} <ChevronDown size={14} className={expandedSolutionCards.has(s.id) ? "rotated" : ""} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isMobile && (
              <button className="mobile-expand-btn" onClick={() => setSolutionsExpanded(v => !v)}>
                {solutionsExpanded ? "收起行业方案" : "展开全部行业方案"} <ChevronDown size={18} className={`expand-chevron ${solutionsExpanded ? "rotated" : ""}`} />
              </button>
            )}
          </div>
        </ScrollSection>

        {/* PARTNERS */}
        <ScrollSection className="partner-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40, textAlign: "center", justifyContent: "center" }}>
              <div><span className="section-label">合作伙伴</span><h2>与行业领先企业携手共进</h2></div>
            </div>
            <div className="partner-grid">{partners.map(p => <div className="partner-card" key={p}><span>{p}</span></div>)}</div>
          </div>
        </ScrollSection>

        {/* NEWS */}
        <ScrollSection id="news" className="news-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head">
              <div><span className="section-label">新闻资讯</span><h2>关注电力运维与新能源工程动态</h2></div>
              <button className="outline-btn" onClick={() => scrollToSection("news")}>查看全部新闻</button>
            </div>
            <div className="news-grid">
              {newsItems.slice(0, 6).map(n => (
                <article className="news-card" key={n.id} onClick={() => news.some(item => item.id === n.id) ? setHash(`/news/${n.id}`) : null} style={{ cursor: news.some(item => item.id === n.id) ? "pointer" : "default" }}>
                  {n.image && <div className="card-img-wrap"><img src={n.image} alt={n.title} /></div>}
                  <div className="card-body"><time>{n.date || n.updated}</time><h3>{n.title}</h3><p>{n.summary}</p></div>
                </article>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* FAQ */}
        <ScrollSection className="faq-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40, textAlign: "center", justifyContent: "center" }}>
              <div><span className="section-label">常见问题</span><h2>您可能关心的问题</h2></div>
            </div>
            <div className="faq-list">
              {faqItems.map((item, i) => (
                <div className={`faq-item${openFaq === i ? " open" : ""}`} key={i}>
                  <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span>{item.q}</span><ChevronDown size={20} className={`faq-chevron ${openFaq === i ? "rotated" : ""}`} />
                  </button>
                  <div className="faq-answer" style={{ maxHeight: openFaq === i ? "300px" : "0" }}><p>{item.a}</p></div>
                </div>
              ))}
            </div>
          </div>
        </ScrollSection>

        {/* TIMELINE */}
        <ScrollSection className="timeline-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40, textAlign: "center", justifyContent: "center" }}>
              <div><span className="section-label">发展历程</span><h2>十年深耕，砥砺前行</h2></div>
            </div>
            <div className="timeline-hscroll">
              <div className="timeline-scroll-inner">
                <div className="timeline-track-line" />
                {timeline.map((item, i) => (
                  <div className={`timeline-node ${i % 2 === 0 ? "up" : "down"}`} key={item.year}>
                    <div className="timeline-dot" />
                    <div className="timeline-card">
                      <span className="timeline-year">{item.year}</span>
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollSection>

        {/* CAREERS */}
        <ScrollSection id="careers" className="careers-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40 }}>
              <div><span className="section-label">人才招聘</span><h2>加入我们，共创未来</h2></div>
            </div>
            <div className={`career-grid ${isMobile && !careersExpanded ? "careers-collapsed" : ""}`}>
              {careers.map(job => (
                <div className={`career-card ${isMobile && expandedCareerCards.has(job.id) ? "card-expanded" : ""}`} key={job.id}>
                  <div className="career-top">
                    <h3>{job.title}</h3>
                    <span className="career-dept"><Briefcase size={14} /> {job.dept}</span>
                  </div>
                  <div className="career-meta">
                    <span><MapPin size={14} /> {job.location}</span>
                    <span><Clock size={14} /> {job.type}</span>
                    <span className="career-salary">{job.salary}</span>
                  </div>
                  <p>{job.desc}</p>
                  <div className="career-reqs">
                    {job.requirements.map((r, i) => <span key={i} className="career-req-tag">{r}</span>)}
                  </div>
                  <button className="outline-btn" onClick={() => scrollToSection("contact")}>投递简历</button>
                  {isMobile && (
                    <button className="card-expand-toggle" onClick={() => toggleCareerCard(job.id)}>
                      {expandedCareerCards.has(job.id) ? "收起" : "展开详情"} <ChevronDown size={14} className={expandedCareerCards.has(job.id) ? "rotated" : ""} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isMobile && (
              <button className="mobile-expand-btn" onClick={() => setCareersExpanded(v => !v)}>
                {careersExpanded ? "收起招聘信息" : "展开全部招聘信息"} <ChevronDown size={18} className={`expand-chevron ${careersExpanded ? "rotated" : ""}`} />
              </button>
            )}
          </div>
        </ScrollSection>

        {/* DOWNLOADS */}
        <ScrollSection id="downloads" className="downloads-section section-pad" delay={50}>
          <div className="container">
            <div className="section-head" style={{ marginBottom: 40 }}>
              <div><span className="section-label">下载中心</span><h2>资料下载与技术文档</h2></div>
            </div>
            <div className={`downloads-grid ${isMobile && !downloadsExpanded ? "downloads-collapsed" : ""}`}>
              {downloads.map(dl => (
                <div className="download-card" key={dl.id}>
                  <div className="download-icon"><Download size={24} /></div>
                  <div>
                    <h3>{dl.title}</h3>
                    <span>{dl.type} · {dl.size}</span>
                  </div>
              <button className="outline-btn compact" onClick={(e) => { createRipple(e); handleDownload(dl); }}>申请资料</button>
                </div>
              ))}
            </div>
            {isMobile && (
              <button className="mobile-expand-btn" onClick={() => setDownloadsExpanded(v => !v)}>
                {downloadsExpanded ? "收起下载列表" : "展开全部下载资料"} <ChevronDown size={18} className={`expand-chevron ${downloadsExpanded ? "rotated" : ""}`} />
              </button>
            )}
            {downloadToast && <div className="download-toast">{downloadToast}</div>}
          </div>
        </ScrollSection>

        {/* NEWSLETTER */}
        <section className="newsletter-section">
          <div className="container newsletter-inner">
            <div>
              <h2>订阅行业资讯</h2>
              <p>获取最新的电力运维技术动态、行业政策和新能源发展资讯。</p>
            </div>
            <form className="newsletter-form" onSubmit={submitSubscription}>
              <input type="email" value={newsEmail} onChange={e => setNewsEmail(e.target.value)} placeholder="请输入您的邮箱地址" required />
              <button className="primary-btn" type="submit" disabled={newsSubscribeStatus.loading}><Send size={18} /> {newsSubscribeStatus.loading ? "提交中" : "订阅"}</button>
              {newsSubscribeStatus.error && <p className="form-error" role="alert">{newsSubscribeStatus.error}</p>}
              {newsSubscribed && <p className="form-success">订阅成功！感谢您的关注。</p>}
            </form>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="contact-section">
          <div className="container contact-grid">
            <div className="contact-copy">
              <h2>获取专属电力服务方案</h2>
              <p>提交需求后将生成询盘编号，服务顾问会按照您留下的联系方式进行需求确认。</p>
              <div className="contact-methods">
                <span><Phone size={18} /> {siteSettings.hotline}</span>
                <span><Mail size={18} /> {siteSettings.email}</span>
              </div>
            </div>
            <form className="consult-form" onSubmit={submitConsultation}>
              <label>姓名<input value={consultation.name} onChange={e => setConsultation({ ...consultation, name: e.target.value })} placeholder="请输入联系人" required /></label>
              <label>公司<input value={consultation.company} onChange={e => setConsultation({ ...consultation, company: e.target.value })} placeholder="请输入公司名称" required /></label>
              <label>电话<input value={consultation.phone} onChange={e => setConsultation({ ...consultation, phone: e.target.value })} placeholder="请输入联系电话" required /></label>
              <label>服务需求<select value={consultation.service} onChange={e => setConsultation({ ...consultation, service: e.target.value })}>{services.map(s => <option key={s.title}>{s.title}</option>)}</select></label>
              <button className="primary-btn" type="submit" disabled={submitStatus.loading}>{submitStatus.loading ? "提交中…" : "提交需求"} <ChevronRight size={18} /></button>
              {submitStatus.error && <p className="form-error" role="alert">{submitStatus.error}</p>}
              {submitted && <p className="form-success" role="status">提交成功，询盘编号：{submitStatus.reference}。服务顾问将尽快联系您。</p>}
            </form>
          </div>
        </section>
      </main>
      {footer}
      <BackToTop />
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
      {/* Customer Service Widget */}
      <button className="chat-widget" onClick={() => setChatOpen(!chatOpen)} aria-label="在线客服">
        <MessageCircle size={24} />
      </button>
      {chatOpen && (
        <div className="chat-panel">
          <div className="chat-header"><h3>在线客服</h3><button onClick={() => setChatOpen(false)}><X size={18} /></button></div>
          <div className="chat-body">
            <div className="chat-message bot"><p>您好！欢迎咨询华能智维电力。请留下您的需求，技术顾问将尽快回复。</p></div>
          </div>
          <div className="chat-footer">
            <input placeholder="输入您的问题..." />
            <button className="chat-send">发送</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================================================================
   DETAIL PAGES
   =================================================================== */

/* ─── Breadcrumb ─── */
function Breadcrumb({ items }) {
  return (
    <nav className="breadcrumb" aria-label="面包屑导航">
      {items.map((item, i) => (
        <span key={i}>
          {i > 0 && <ChevronRight size={14} className="breadcrumb-sep" />}
          {item.link ? <button onClick={() => setHash(item.link)}>{item.label}</button> : <span className="breadcrumb-current">{item.label}</span>}
        </span>
      ))}
    </nav>
  );
}

/* ─── Service Detail Page ─── */
function ServiceDetailPage({ serviceId }) {
  const svc = services.find(s => s.id === serviceId) || services[0];
  const relatedCases = cases.filter(c => {
    const map = { "power-testing": "变电运维", "substation-ops": "变电运维", "equipment-service": "检修安装", "system-upgrade": "光伏储能" };
    return c.category === (map[svc.id] || c.category);
  }).slice(0, 3);
  return (
    <main className="detail-page">
      <div className="container">
        <Breadcrumb items={[{ label: "首页", link: "/" }, { label: "服务项目", link: "/" }, { label: svc.title }]} />
        <div className="detail-hero">
          <h1>{svc.title}</h1>
          <p className="detail-subtitle">{svc.detail}</p>
          <div className="detail-metric-badge"><strong>{svc.metric}</strong><span>{svc.metricLabel}</span></div>
        </div>
        <div className="detail-content">
          <div className="detail-main">
            <h2>服务介绍</h2>
            {svc.fullDesc.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            <h2>服务流程</h2>
            <div className="detail-process">
              {svc.process.map((step, i) => (
                <div className="detail-step" key={i}><span className="detail-step-num">{i + 1}</span><p>{step}</p></div>
              ))}
            </div>
          </div>
          <aside className="detail-sidebar">
            <div className="detail-card">
              <h3>服务亮点</h3>
              <ul>{svc.features.map(f => <li key={f}><CheckCircle2 size={16} color="#27b875" />{f}</li>)}</ul>
            </div>
            <div className="detail-card">
              <h3>相关案例</h3>
              {relatedCases.map(c => (
                <button key={c.id} className="detail-related-link" onClick={() => setHash(`/case/${c.id}`)}>
                  <span>{c.title}</span><ArrowRight size={14} />
                </button>
              ))}
            </div>
            <button className="primary-btn detail-cta" onClick={() => { setHash("/"); setTimeout(() => scrollToSection("contact"), 100); }}>立即咨询 <ArrowRight size={18} /></button>
          </aside>
        </div>
      </div>
    </main>
  );
}

/* ─── Case Detail Page ─── */
function CaseDetailPage({ caseId }) {
  const item = cases.find(c => c.id === caseId) || cases[0];
  return (
    <main className="detail-page">
      <div className="container">
        <Breadcrumb items={[{ label: "首页", link: "/" }, { label: "工程案例", link: "/" }, { label: item.title }]} />
        <div className="detail-hero case-hero">
          <span className="card-tag" style={{ marginBottom: 16, display: "inline-flex" }}>{item.category}</span>
          <h1>{item.title}</h1>
          <p className="detail-subtitle">{item.result}</p>
        </div>
        <div className="case-meta-grid">
          <div><span>客户</span><strong>{item.client}</strong></div>
          <div><span>周期</span><strong>{item.period}</strong></div>
          <div><span>规模</span><strong>{item.scale}</strong></div>
        </div>
        <div className="detail-content">
          <div className="detail-main">
            <h2>项目挑战</h2><p>{item.challenge}</p>
            <h2>解决方案</h2><p>{item.solution}</p>
            <h2>项目成果</h2>
            <ul className="achievement-list">
              {item.achievement.map((a, i) => <li key={i}><Trophy size={18} color="#ff8a1f" />{a}</li>)}
            </ul>
          </div>
          <aside className="detail-sidebar">
            <div className="detail-card">
              <h3>更多案例</h3>
              {cases.filter(c => c.id !== item.id).slice(0, 3).map(c => (
                <button key={c.id} className="detail-related-link" onClick={() => setHash(`/case/${c.id}`)}>
                  <span>{c.title}</span><ArrowRight size={14} />
                </button>
              ))}
            </div>
            <button className="primary-btn detail-cta" onClick={() => { setHash("/"); setTimeout(() => scrollToSection("contact"), 100); }}>咨询类似方案 <ArrowRight size={18} /></button>
          </aside>
        </div>
      </div>
    </main>
  );
}

/* ─── News Detail Page ─── */
function NewsDetailPage({ newsId }) {
  const item = news.find(n => n.id === newsId) || news[0];
  return (
    <main className="detail-page">
      <div className="container">
        <Breadcrumb items={[{ label: "首页", link: "/" }, { label: "新闻资讯", link: "/" }, { label: item.title }]} />
        <div className="detail-hero news-hero">
          <span className="card-tag" style={{ marginBottom: 16, display: "inline-flex" }}>{item.category}</span>
          <h1>{item.title}</h1>
          <div className="news-meta"><Calendar size={16} /><time>{item.date}</time></div>
        </div>
        <div className="detail-content news-content">
          <div className="detail-main">
            {item.content.split("\n\n").map((p, i) => {
              if (p.startsWith("•") || p.startsWith("-")) {
                return <ul key={i}>{p.split("\n").filter(l => l.trim()).map((li, j) => <li key={j}>{li.replace(/^[•\-]\s*/, "")}</li>)}</ul>;
              }
              return <p key={i}>{p}</p>;
            })}
          </div>
          <aside className="detail-sidebar">
            <div className="detail-card">
              <h3>相关新闻</h3>
              {news.filter(n => n.id !== item.id).slice(0, 3).map(n => (
                <button key={n.id} className="detail-related-link" onClick={() => setHash(`/news/${n.id}`)}>
                  <span>{n.title}</span><ArrowRight size={14} />
                </button>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function AdminLogin({ onAuthenticated }) {
  const [form, setForm] = useState({ username: "admin", password: "" });
  const [status, setStatus] = useState({ loading: false, error: "" });

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ loading: true, error: "" });
    try {
      const result = await api.login(form.username, form.password);
      onAuthenticated(result.user);
    } catch (error) {
      setStatus({ loading: false, error: error.message });
    }
  };

  return (
    <main className="login-shell">
      <section className="login-brand-panel">
        <Brand inverted />
        <div><h1>内容与询盘管理中心</h1><p>统一管理官网询盘、服务内容、工程案例、新闻资讯与站点设置。</p></div>
        <ul><li><ShieldCheck size={18} /> HttpOnly 管理会话</li><li><Database size={18} /> 服务端持久化数据</li><li><Activity size={18} /> 官网与控制台实时联动</li></ul>
      </section>
      <section className="login-form-panel">
        <form className="login-form" onSubmit={submit}>
          <span className="login-icon"><LockKeyhole size={24} /></span>
          <h2>登录管理后台</h2><p>请输入管理员凭据继续。</p>
          <label>账号<input autoComplete="username" value={form.username} onChange={event => setForm({ ...form, username: event.target.value })} required /></label>
          <label>密码<input type="password" autoComplete="current-password" value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} required autoFocus /></label>
          {status.error && <p className="form-error" role="alert">{status.error}</p>}
          <button className="console-primary" type="submit" disabled={status.loading}>{status.loading ? "登录中…" : "安全登录"}</button>
          <button className="login-back" type="button" onClick={() => setHash("/")}><ChevronLeft size={16} /> 返回官网</button>
          {import.meta.env.DEV && <p className="dev-credential"><AlertTriangle size={15} /> 本机开发账号：admin / admin123</p>}
        </form>
      </section>
    </main>
  );
}

/* ===================================================================
   CONSOLE APP
   =================================================================== */
function ConsoleApp({ siteSettings, onUpdateSiteSettings, currentUser, onLogout }) {
  const [active, setActive] = useState("数据看板");
  const [collapsed, setCollapsed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [toast, setToast] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);
  const [contentItems, setContentItems] = useState({ 服务项目: [], 工程案例: [], 新闻资讯: [], 资质证书: [] });
  const [projectItems, setProjectItems] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [systemStatus, setSystemStatus] = useState({ loading: true, error: "", updatedAt: "" });

  const unreadCount = notifications.filter(n => !n.read).length;
  const showToast = (msg) => setToast(msg);
  const dismissToast = () => setToast(null);

  useEffect(() => {
    let active = true;
    api.adminState().then(result => {
      if (!active) return;
      setLeads(result.state.leads || []);
      setContentItems(result.state.content || {});
      setProjectItems(result.state.projects || []);
      setSubscribers(result.state.subscribers || []);
      if (result.state.settings) onUpdateSiteSettings(result.state.settings, { remote: true });
      if (result.state.notifications?.length) setNotifications(result.state.notifications);
      setSystemStatus({ loading: false, error: "", updatedAt: result.state.updatedAt });
    }).catch(error => {
      if (active) setSystemStatus({ loading: false, error: error.message, updatedAt: "" });
    });
    return () => { active = false; };
  }, [onUpdateSiteSettings]);

  const publishItem = async (section, titleOrItem) => {
    const item = typeof titleOrItem === "string"
      ? { id: Date.now(), title: titleOrItem, summary: "", image: "", status: section === "资质证书" ? "待审核" : "草稿", updated: new Date().toISOString().slice(0, 10) }
      : { id: Date.now(), ...titleOrItem, updated: new Date().toISOString().slice(0, 10) };
    if (!item.title || !item.title.trim()) return;
    try {
      const result = await api.createContent(section, item);
      setContentItems(items => ({ ...items, [section]: [result.item, ...(items[section] || [])] }));
      return result.item;
    } catch (error) { showToast(error.message); throw error; }
  };
  const updateContentItem = async (section, updatedItem) => {
    const result = await api.updateContent(section, updatedItem.id, updatedItem);
    setContentItems(items => ({ ...items, [section]: items[section].map(item => item.id === result.item.id ? result.item : item) }));
  };
  const deleteContentItem = async (section, id) => {
    await api.deleteContent(section, id);
    setContentItems(items => ({ ...items, [section]: items[section].filter(item => item.id !== id) }));
  };
  const publishContentItem = async (section, id) => {
    const item = contentItems[section].find(entry => entry.id === id);
    if (!item) return;
    const result = await api.updateContent(section, id, { ...item, status: "已发布" });
    setContentItems(items => ({ ...items, [section]: items[section].map(entry => entry.id === id ? result.item : entry) }));
  };
  const updateLeadStatus = async (id, status) => {
    try {
      const result = await api.updateLead(id, { status });
      setLeads(items => items.map(item => item.id === id ? result.lead : item));
      showToast(`询盘 ${id} 已更新为“${status}”`);
    } catch (error) { showToast(error.message); }
  };
  const updateLead = async (id, changes) => {
    try {
      const result = await api.updateLead(id, changes);
      setLeads(items => items.map(item => item.id === id ? result.lead : item));
      showToast(`询盘 ${id} 已保存`);
      return result.lead;
    } catch (error) { showToast(error.message); throw error; }
  };
  const deleteLead = async (lead) => {
    if (!window.confirm(`确定删除 ${lead.company} 的询盘吗？`)) return;
    try {
      await api.deleteLead(lead.id);
      setLeads(items => items.filter(item => item.id !== lead.id));
      showToast(`询盘 ${lead.id} 已删除`);
    } catch (error) { showToast(error.message); throw error; }
  };
  const updateProject = async (id, changes) => {
    try {
      const result = await api.updateProject(id, changes);
      setProjectItems(items => items.map(item => item.id === id ? result.project : item));
      showToast(`项目“${result.project.title}”已更新`);
    } catch (error) { showToast(error.message); }
  };
  const deleteSubscriber = async (subscriber) => {
    if (!window.confirm(`确定移除订阅邮箱 ${subscriber.email} 吗？`)) return;
    try { await api.deleteSubscriber(subscriber.id); setSubscribers(items => items.filter(item => item.id !== subscriber.id)); showToast("订阅记录已移除"); } catch (error) { showToast(error.message); }
  };

  const markAllNotificationsRead = async () => { try { const result = await api.markNotificationsRead(); setNotifications(result.notifications); setNotifyOpen(false); showToast("已将所有通知标记为已读"); } catch (error) { showToast(error.message); } };
  const navigateTo = (section) => { setActive(section); setNotifyOpen(false); setCollapsed(false); };

  useEffect(() => { if (notifyOpen) { const cb = (e) => { if (e.key === "Escape") setNotifyOpen(false); }; window.addEventListener("keydown", cb); return () => window.removeEventListener("keydown", cb); } }, [notifyOpen]);

  return (
    <div className={collapsed ? "console-shell collapsed" : "console-shell"}>
      {collapsed && <div className="sidebar-overlay" onClick={() => setCollapsed(false)} />}
      <aside className="console-sidebar">
        <Brand inverted settings={siteSettings} />
        <nav>
          {["数据看板", "询盘管理", "项目管理", "服务项目", "工程案例", "新闻资讯", "资质证书", "订阅管理", "站点设置"].map(item => (
            <button className={active === item ? "active" : ""} key={item} onClick={() => navigateTo(item)}>
              {consoleIcon(item)} <span>{item}</span>
            </button>
          ))}
        </nav>
        <button className="collapse-btn" onClick={() => setCollapsed(v => !v)}><Menu size={20} /><span>{collapsed ? "展开菜单" : "收起菜单"}</span></button>
      </aside>
      <div className="console-main" onClick={() => collapsed && setCollapsed(false)}>
        <header className="console-topbar">
          <button className="icon-button" onClick={() => setCollapsed(v => !v)} aria-label="切换菜单"><Menu size={22} /></button>
          <h1>{active}</h1>
          <div className="console-search"><Search size={18} /><input placeholder="搜索询盘、项目、内容等" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => { if (e.key !== "Enter" || !searchQuery.trim()) return; const q = searchQuery.trim().toLowerCase(); const leadMatch = leads.some(item => Object.values(item).some(value => String(value).toLowerCase().includes(q))); const projectMatch = projectItems.some(item => Object.values(item).some(value => String(value).toLowerCase().includes(q))); const contentSection = Object.entries(contentItems).find(([, items]) => items.some(item => `${item.title} ${item.summary || ""}`.toLowerCase().includes(q)))?.[0]; if (leadMatch) setActive("询盘管理"); else if (projectMatch) setActive("项目管理"); else if (contentSection) setActive(contentSection); else showToast(`未找到与“${searchQuery.trim()}”相关的数据`); setSearchQuery(""); }} /></div>
          <div className="notify-wrap">
            <button className="icon-button" aria-label="通知" onClick={() => setNotifyOpen(v => !v)}><Bell size={20} />{unreadCount > 0 && <span className="dot">{unreadCount}</span>}</button>
            {notifyOpen && <NotificationPanel notifications={notifications} onMarkAllRead={markAllNotificationsRead} onClose={() => setNotifyOpen(false)} />}
          </div>
          <span className="data-mode"><Database size={15} /> 服务端数据</span>
          <button className="admin-button" onClick={() => showToast(`管理员：${currentUser.username}，角色：${currentUser.role}`)}><UserRound size={20} /> {currentUser.username}</button>
          <button className="icon-button logout-button" aria-label="退出登录" onClick={onLogout}><LogOut size={19} /></button>
        </header>
        <main className="console-content">
          {!systemStatus.loading && !systemStatus.error && <div className="demo-data-banner"><Database size={16} /><span>当前数据集包含项目内置演示内容；新询盘、草稿、设置和状态变更会真实保存到本机服务端。</span></div>}
          {systemStatus.loading && <div className="console-state"><RefreshCw className="spin" /><strong>正在载入业务数据…</strong></div>}
          {systemStatus.error && <div className="console-state error"><AlertTriangle /><strong>数据载入失败</strong><p>{systemStatus.error}</p><button onClick={() => window.location.reload()}>重新载入</button></div>}
          {!systemStatus.loading && !systemStatus.error && active === "数据看板" && <Dashboard leads={leads} contentItems={contentItems} projectItems={projectItems} onNavigate={setActive} showToast={showToast} publishItem={publishItem} />}
          {!systemStatus.loading && !systemStatus.error && active === "询盘管理" && <LeadManager leads={leads} updateLeadStatus={updateLeadStatus} updateLead={updateLead} deleteLead={deleteLead} />}
          {!systemStatus.loading && !systemStatus.error && ["服务项目", "工程案例", "新闻资讯", "资质证书"].includes(active) && (
            <ContentManager section={active} items={contentItems[active]} publishItem={publishItem} updateContentItem={updateContentItem} deleteContentItem={deleteContentItem} publishContentItem={publishContentItem} showToast={showToast} />
          )}
          {!systemStatus.loading && !systemStatus.error && active === "项目管理" && <ProjectManager items={projectItems} onUpdate={updateProject} />}
          {!systemStatus.loading && !systemStatus.error && active === "订阅管理" && <SubscriberManager items={subscribers} onDelete={deleteSubscriber} />}
          {!systemStatus.loading && !systemStatus.error && active === "站点设置" && <SiteSettings settings={siteSettings} onSaveSettings={onUpdateSiteSettings} onExport={downloadAdminBackup} />}
        </main>
      </div>
      {toast && <Toast message={toast} onDone={dismissToast} />}
    </div>
  );
}

function consoleIcon(name) {
  const p = { size: 19 };
  return { 数据看板: <LayoutDashboard {...p} />, 询盘管理: <MessageCircle {...p} />, 项目管理: <Briefcase {...p} />, 服务项目: <Gauge {...p} />, 工程案例: <Building2 {...p} />, 新闻资讯: <FileText {...p} />, 资质证书: <FileCheck2 {...p} />, 订阅管理: <Mail {...p} />, 站点设置: <Settings {...p} /> }[name];
}

function Dashboard({ leads, contentItems, projectItems, onNavigate, showToast, publishItem }) {
  const sourceData = useMemo(() => {
    const counts = new Map();
    leads.forEach(lead => counts.set(lead.source || "其他渠道", (counts.get(lead.source || "其他渠道") || 0) + 1));
    return [...counts].map(([name, value]) => ({ name, value }));
  }, [leads]);
  const trendData = useMemo(() => {
    const grouped = new Map();
    leads.forEach(lead => {
      const match = String(lead.time).match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (!match) return;
      const day = `${match[2].padStart(2, "0")}-${match[3].padStart(2, "0")}`;
      const current = grouped.get(day) || { day, total: 0, valid: 0 };
      current.total += 1;
      if (lead.status !== "新询盘") current.valid += 1;
      grouped.set(day, current);
    });
    return [...grouped.values()].sort((a, b) => a.day.localeCompare(b.day)).slice(-10);
  }, [leads]);
  const publishedCount = section => (contentItems[section] || []).filter(item => ["已发布", "有效"].includes(item.status)).length;
  const kpis = [
    ["询盘总数", String(leads.length), "服务端实时数据", MessageCircle, "blue", "询盘管理"],
    ["待跟进询盘", String(leads.filter(item => ["新询盘", "跟进中"].includes(item.status)).length), "需要销售处理", UserRound, "green", "询盘管理"],
    ["已发布服务", String(publishedCount("服务项目")), "官网公开内容", ClipboardCheck, "orange", "服务项目"],
    ["工程案例", String(publishedCount("工程案例")), "官网公开内容", Activity, "purple", "工程案例"],
    ["新闻资讯", String(publishedCount("新闻资讯")), "官网公开内容", FileText, "teal", "新闻资讯"],
    ["有效资质", String(publishedCount("资质证书")), "官网公开内容", FileCheck2, "blue", "资质证书"],
  ];
  return (
    <>
      <div className="kpi-grid">
        {kpis.map(([label, value, trend, Icon, tone, nav]) => (
          <article className="kpi-card" key={label} onClick={() => onNavigate(nav)}>
            <span className={`kpi-icon ${tone}`}><Icon size={25} /></span>
            <div><span>{label}</span><strong>{value}</strong><em>{trend}</em></div>
          </article>
        ))}
      </div>
      <div className="dashboard-grid">
        <section className="console-panel chart-panel">
          <PanelTitle title="询盘趋势（本月）" />
          <TrendChart data={trendData} />
        </section>
        <section className="console-panel source-panel">
          <PanelTitle title="询盘来源分布（本月）" />
          <div className="source-grid">
            <SourceDonut data={sourceData} />
            <div className="source-list">{sourceData.length ? sourceData.map((item, i) => <div key={item.name}><span style={{ background: sourceColors[i % sourceColors.length] }} /><strong>{item.name}</strong><em>{item.value}</em></div>) : <p className="source-empty">暂无来源数据</p>}</div>
          </div>
        </section>
        <QuickPublish publishItem={publishItem} />
        <section className="console-panel wide-panel">
          <PanelTitle title="最新询盘" action="查看全部询盘" onAction={() => onNavigate("询盘管理")} />
          <LeadTable leads={leads.slice(0, 5)} onOpen={() => onNavigate("询盘管理")} compact />
        </section>
        <section className="console-panel">
          <PanelTitle title="项目进度" action="查看全部项目" onAction={() => onNavigate("项目管理")} />
          <div className="project-list">
            {projectItems.slice(0, 4).map(p => (
              <article key={p.title}><img src={p.image} alt={p.title} /><div><h3>{p.title}</h3><div className="progress-row"><span>进度</span><div className="progress-track"><i style={{ width: `${p.progress}%` }} /></div><em>{p.progress}%</em></div><p>计划完成：{p.due}</p></div><strong>{p.status}</strong></article>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function QuickPublish({ publishItem }) {
  const [type, setType] = useState("新闻资讯"); const [title, setTitle] = useState(""); const [msg, setMsg] = useState(""); const [saving, setSaving] = useState(false);
  return (
    <section className="console-panel quick-panel">
      <PanelTitle title="快速发布" />
      <form onSubmit={async e => { e.preventDefault(); if (!title.trim()) return; setSaving(true); setMsg(""); try { await publishItem(type, title); setMsg(`${type}「${title}」已加入草稿列表`); setTitle(""); } catch { setMsg("保存失败，请稍后重试"); } finally { setSaving(false); } }}>
        <label>发布类型<select value={type} onChange={e => setType(e.target.value)}><option>新闻资讯</option><option>工程案例</option><option>服务项目</option><option>资质证书</option></select></label>
        <label>标题<input value={title} onChange={e => setTitle(e.target.value)} placeholder="输入内容标题" /></label>
        <button className="console-primary" type="submit" disabled={saving}>{saving ? <RefreshCw size={17} className="spin" /> : <Upload size={17} />} {saving ? "保存中" : "保存草稿"}</button>
        {msg && <p>{msg}</p>}
      </form>
    </section>
  );
}

function LeadDetailModal({ lead, onSave, onDelete, onClose }) {
  const [note, setNote] = useState(lead.note || "");
  const [saving, setSaving] = useState(false);
  return <div className="modal-overlay" onClick={onClose}><section className="modal-card lead-detail-modal" onClick={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="lead-detail-title"><div className="modal-header"><h3 id="lead-detail-title">询盘详情</h3><button className="modal-close" onClick={onClose}><X size={20} /></button></div><dl><div><dt>询盘编号</dt><dd>{lead.id}</dd></div><div><dt>企业名称</dt><dd>{lead.company}</dd></div><div><dt>联系人</dt><dd>{lead.contact}</dd></div><div><dt>联系电话</dt><dd>{lead.phone}</dd></div><div><dt>服务需求</dt><dd>{lead.service || "未填写"}</dd></div><div><dt>提交时间</dt><dd>{lead.time}</dd></div></dl><label>跟进备注<textarea value={note} onChange={event => setNote(event.target.value)} rows="5" placeholder="记录沟通结果、下一步计划或负责人" /></label><div className="modal-actions"><button className="console-primary" disabled={saving} onClick={async () => { setSaving(true); try { await onSave(lead.id, { note }); onClose(); } finally { setSaving(false); } }}>保存备注</button><button className="btn-delete" disabled={saving} onClick={() => onDelete(lead)}>删除询盘</button><button className="ghost-button" onClick={onClose}>关闭</button></div></section></div>;
}

function SubscriberManager({ items, onDelete }) {
  return <section className="console-panel management-panel"><PanelTitle title="资讯订阅管理" /><p className="manager-intro">当前仅记录订阅意向；系统未配置邮件发送服务，不会伪造邮件发送成功。</p>{items.length === 0 ? <div className="empty-state"><Mail size={26} /><strong>暂无订阅记录</strong><p>官网订阅表单提交后会显示在这里。</p></div> : <div className="table-wrap"><table className="lead-table"><thead><tr><th>邮箱地址</th><th>订阅时间</th><th>状态</th><th>操作</th></tr></thead><tbody>{items.map(item => <tr key={item.id}><td>{item.email}</td><td>{new Date(item.time).toLocaleString("zh-CN", { hour12: false })}</td><td><span className="status published">{item.status}</span></td><td><button className="btn-delete" onClick={() => onDelete(item)}>移除</button></td></tr>)}</tbody></table></div>}</section>;
}

function ProjectManager({ items, onUpdate }) {
  return <section className="console-panel management-panel project-manager"><PanelTitle title="项目进度管理" /><p className="manager-intro">进度和状态保存后将同步到数据看板。</p><div className="project-manager-list">{items.map(project => <article key={project.id}><img src={project.image} alt="" /><div className="project-editor-main"><h3>{project.title}</h3><div className="project-editor-fields"><label>进度<input type="range" min="0" max="100" value={project.progress} onChange={event => onUpdate(project.id, { progress: Number(event.target.value) })} /><strong>{project.progress}%</strong></label><label>状态<select value={project.status} onChange={event => onUpdate(project.id, { status: event.target.value })}><option>准备中</option><option>施工中</option><option>调试中</option><option>已验收</option><option>已延期</option></select></label><label>计划完成<input type="date" value={project.due} onChange={event => onUpdate(project.id, { due: event.target.value })} /></label></div></div></article>)}</div></section>;
}

function LeadManager({ leads, updateLeadStatus, updateLead, deleteLead }) {
  const [filter, setFilter] = useState("全部");
  const [selectedLead, setSelectedLead] = useState(null);
  const filtered = useMemo(() => filter === "全部" ? leads : leads.filter(l => l.status === filter), [filter, leads]);
  return (
    <section className="console-panel management-panel">
      <div className="manager-toolbar"><PanelTitle title="询盘列表" />
        <div className="segment-control">{["全部", "新询盘", "跟进中", "已回复", "已转化"].map(s => <button className={filter === s ? "active" : ""} key={s} onClick={() => setFilter(s)}>{s}</button>)}</div>
      </div>
      <LeadTable leads={filtered} updateLeadStatus={updateLeadStatus} onOpen={setSelectedLead} />
      {selectedLead && <LeadDetailModal lead={selectedLead} onSave={updateLead} onDelete={deleteLead} onClose={() => setSelectedLead(null)} />}
    </section>
  );
}

function LeadTable({ leads, updateLeadStatus, compact = false, onOpen }) {
  return (
    <div className="table-wrap">
      {leads.length === 0 && <div className="empty-state"><MessageCircle size={26} /><strong>暂无符合条件的询盘</strong><p>新提交的官网需求会显示在这里。</p></div>}
      <table className="lead-table">
        <thead><tr><th>询盘编号</th><th>客户名称</th><th>联系人</th><th>联系方式</th><th>来源</th><th>询盘时间</th><th>状态</th><th>操作</th></tr></thead>
        <tbody>
          {leads.map(l => (
            <tr key={l.id}><td>{l.id}</td><td>{l.company}</td><td>{l.contact}</td><td>{l.phone}</td><td>{l.source}</td><td>{l.time}</td>
              <td><span className={`status ${statusClass(l.status)}`}>{l.status}</span></td>
              <td><div className="table-actions">{onOpen && <button onClick={() => onOpen(l)}>详情</button>}<button onClick={() => updateLeadStatus?.(l.id, "跟进中")}>跟进</button>{!compact && <button onClick={() => updateLeadStatus?.(l.id, "已转化")}>转化</button>}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function statusClass(s) { return { 新询盘: "new", 跟进中: "pending", 已回复: "done", 已转化: "converted", 草稿: "draft", 已发布: "published", 有效: "published", 待审核: "pending" }[s]; }

function ContentManager({ section, items, publishItem, updateContentItem, deleteContentItem, publishContentItem, showToast }) {
  const [title, setTitle] = useState(""); const [summary, setSummary] = useState(""); const [image, setImage] = useState(""); const [preview, setPreview] = useState(""); const [editItem, setEditItem] = useState(null);
  const handleImg = (e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = ev => { setImage(ev.target.result); setPreview(ev.target.result); }; r.readAsDataURL(f); } };
  const build = () => ({ title: title.trim(), summary: summary.trim(), image: preview, status: section === "资质证书" ? "待审核" : "草稿" });
  const reset = () => { setTitle(""); setSummary(""); setImage(""); setPreview(""); };
  const draft = async (e) => { e.preventDefault(); if (!title.trim()) return; try { await publishItem(section, build()); showToast(`${section}「${title.trim()}」已保存为草稿`); reset(); } catch { /* ConsoleApp reports error */ } };
  const pub = async (e) => { e.preventDefault(); if (!title.trim()) return; try { await publishItem(section, { ...build(), status: "已发布" }); showToast(`${section}「${title.trim()}」已直接发布`); reset(); } catch { /* ConsoleApp reports error */ } };
  const save = async (updated, shouldPublish) => { try { await updateContentItem(section, { ...updated, status: shouldPublish ? "已发布" : updated.status }); showToast(shouldPublish ? `「${updated.title}」已更新并发布` : `「${updated.title}」已更新`); setEditItem(null); } catch (error) { showToast(error.message); } };
  const del = async (id, t) => { if (!window.confirm(`确定删除“${t}”吗？此操作会同步到服务端。`)) return; try { await deleteContentItem(section, id); showToast(`已删除「${t}」`); } catch (error) { showToast(error.message); } };
  const pubItem = async (id, t) => { try { await publishContentItem(section, id); showToast(`「${t}」已发布`); } catch (error) { showToast(error.message); } };
  return (
    <div className="content-layout">
      <section className="console-panel management-panel">
        <PanelTitle title={`${section}管理`} />
        <div className="content-list">
          {items.length === 0 && <div className="empty-state"><FileText size={26} /><strong>暂无{section}</strong><p>可在右侧创建第一条内容。</p></div>}
          {items.map(item => (
            <article key={item.id}>
              {item.image && <div className="content-thumb"><img src={item.image} alt={item.title} /></div>}
              <div><h3>{item.title}</h3><p>最后更新：{item.updated}</p></div>
              <span className={`status ${statusClass(item.status)}`}>{item.status}</span>
              <div className="table-actions"><button onClick={() => setEditItem(item)}>编辑</button><button onClick={() => showToast(`预览：${item.title}`)}>预览</button><button className="btn-publish" onClick={() => pubItem(item.id, item.title)}>发布</button><button className="btn-delete" onClick={() => del(item.id, item.title)}>删除</button></div>
            </article>
          ))}
        </div>
      </section>
      <section className="console-panel editor-panel">
        <PanelTitle title="新增内容" />
        <form onSubmit={draft}>
          <label>标题<input value={title} onChange={e => setTitle(e.target.value)} placeholder={`输入${section}标题`} required /></label>
          <label>摘要<textarea value={summary} onChange={e => setSummary(e.target.value)} placeholder="输入内容摘要" rows="5" /></label>
          <label>图片</label>
          {preview ? <div className="image-preview"><img src={preview} alt="Preview" /><button type="button" className="image-remove" onClick={() => { setImage(""); setPreview(""); }}>移除图片</button></div>
            : <label className="upload-box"><Upload size={22} />上传封面<input type="file" accept="image/*" onChange={handleImg} /></label>}
          <div className="modal-actions"><button className="console-primary" type="submit"><FileText size={17} /> 保存草稿</button><button className="btn-publish" type="button" onClick={pub}><Upload size={17} /> 直接发布</button></div>
        </form>
      </section>
      {editItem && <EditModal item={editItem} section={section} onSave={save} onCancel={() => setEditItem(null)} />}
    </div>
  );
}

function SiteSettings({ settings, onSaveSettings, onExport }) {
  const [enabled, setEnabled] = useState(settings.publicAccess !== false);
  const [draft, setDraft] = useState(settings);
  const [message, setMessage] = useState("");
  const [animSettings, setAnimSettings] = useState(readAnimSettings);
  const [animMessage, setAnimMessage] = useState("");
  useEffect(() => { setDraft(settings); }, [settings]);
  const updateDraft = (f, v) => { setDraft(c => ({ ...c, [f]: v })); setMessage(""); };
  const save = async (e) => { e.preventDefault(); const n = normalizeSiteSettings({ ...draft, publicAccess: enabled }); try { await onSaveSettings(n); setMessage("设置已保存到服务端。"); } catch (error) { setMessage(error.message); } };
  const saveAnim = () => { saveAnimSettings(animSettings); setAnimMessage("动画效果设置已保存，刷新后生效。"); };
  return (
    <div className="content-layout">
      <section className="console-panel editor-panel settings-panel">
        <PanelTitle title="基础信息" />
        <form onSubmit={save}>
          <label>网站名称<input value={draft.siteName} onChange={e => updateDraft("siteName", e.target.value)} required /></label>
          <label>英文标识<input value={draft.brandEnglish} onChange={e => updateDraft("brandEnglish", e.target.value)} required /></label>
          <label>服务热线<input value={draft.hotline} onChange={e => updateDraft("hotline", e.target.value)} required /></label>
          <label>联系邮箱<input value={draft.email} onChange={e => updateDraft("email", e.target.value)} type="email" required /></label>
          <label>公司地址<input value={draft.address} onChange={e => updateDraft("address", e.target.value)} required /></label>
          <label>备案号<input value={draft.icpRecord} onChange={e => updateDraft("icpRecord", e.target.value)} required /></label>
          <div className="settings-actions"><button className="console-primary" type="submit"><CheckCircle2 size={17} /> 保存设置</button><button className="ghost-button" type="button" onClick={() => { onSaveSettings(DEFAULT_SITE_SETTINGS); setDraft(DEFAULT_SITE_SETTINGS); setMessage("已恢复默认。"); }}>恢复默认</button></div>
          {message && <p className="save-message">{message}</p>}
        </form>
      </section>
      <section className="console-panel editor-panel settings-panel">
        <PanelTitle title="安全设置" />
        <div className="setting-row"><div><h3>验证访问次数</h3><p>同一用户1小时内访问超过此次数需验证（1-20次）</p></div><input type="number" min="1" max="20" value={draft.verificationVisits ?? 5} onChange={e => updateDraft("verificationVisits", parseInt(e.target.value, 10) || 5)} className="number-input" /></div>
        <div className="setting-row" style={{ marginTop: 16 }}><div><h3>网站公开访问</h3><p>关闭后服务端将暂停接收新询盘。</p></div><button type="button" className={enabled ? "toggle on" : "toggle"} onClick={() => setEnabled(v => !v)} aria-pressed={enabled}><span /></button></div>
        <div className="site-health" style={{ marginTop: 16 }}><p><CheckCircle2 size={17} /> API 状态：服务端已连接</p><p><ShieldCheck size={17} /> 访问验证：服务端 IP 计数与题目校验</p><p><Database size={17} /> 数据存储：本机服务端 JSON（单实例）</p></div>
        <button type="button" className="ghost-button export-button" onClick={onExport}><Download size={17} /> 导出系统备份</button>
      </section>
      <section className="console-panel editor-panel settings-panel">
        <PanelTitle title="动画效果设置" />
        <div className="animation-settings">
          <div className="setting-row"><div><h3>启用滚动动画</h3><p>页面元素在滚动时呈现渐入动画</p></div><button className={animSettings.enabled ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, enabled: !c.enabled }))}><span /></button></div>
          <div className="setting-row"><div><h3>动画样式</h3></div><select className="anim-select" value={animSettings.style} onChange={e => setAnimSettings(c => ({ ...c, style: e.target.value }))}><option value="fadeInUp">从下往上淡入</option><option value="fadeInLeft">从左往右淡入</option><option value="fadeInRight">从右往左淡入</option><option value="scaleIn">缩放淡入</option><option value="zoomIn">放大淡入</option></select></div>
          <div className="setting-row"><div><h3>动画速度</h3></div><select className="anim-select" value={animSettings.speed} onChange={e => setAnimSettings(c => ({ ...c, speed: e.target.value }))}><option value="slow">慢速</option><option value="normal">正常</option><option value="fast">快速</option></select></div>
          <div className="setting-row"><div><h3>动画强度</h3><p>控制所有动态效果的力度</p></div><select className="anim-select" value={animSettings.intensity} onChange={e => setAnimSettings(c => ({ ...c, intensity: e.target.value }))}><option value="subtle">微妙</option><option value="medium">标准</option><option value="dramatic">强烈</option></select></div>
          <div className="setting-row"><div><h3>视差滚动背景</h3><p>Hero区域的动态光晕视差效果</p></div><button className={animSettings.parallax ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, parallax: !c.parallax }))}><span /></button></div>
          <div className="setting-row"><div><h3>粒子背景效果</h3><p>全站动态粒子点缀背景</p></div><button className={animSettings.particle ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, particle: !c.particle }))}><span /></button></div>
          <div className="setting-row"><div><h3>3D倾斜卡片</h3><p>鼠标悬停时卡片跟随倾斜</p></div><button className={animSettings.tilt3D ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, tilt3D: !c.tilt3D }))}><span /></button></div>
          <div className="setting-row"><div><h3>浮动图标动画</h3><p>服务图标和证书图标的悬停浮动</p></div><button className={animSettings.floatIcons ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, floatIcons: !c.floatIcons }))}><span /></button></div>
          <div className="setting-row"><div><h3>按钮涟漪效果</h3><p>点击按钮时水纹扩散动效</p></div><button className={animSettings.ripple ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, ripple: !c.ripple }))}><span /></button></div>
          <div className="setting-row"><div><h3>交错入场动画</h3><p>子元素依次延迟出现</p></div><button className={animSettings.stagger ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, stagger: !c.stagger }))}><span /></button></div>
          <div className="setting-row"><div><h3>数字递增动画</h3><p>统计数字滚动递增效果</p></div><button className={animSettings.counter ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, counter: !c.counter }))}><span /></button></div>
          <div className="setting-row"><div><h3>悬停缩放</h3><p>卡片和图片的悬停放大效果</p></div><button className={animSettings.hoverScale ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, hoverScale: !c.hoverScale }))}><span /></button></div>
          <div className="setting-row"><div><h3>光晕脉冲</h3><p>关键数据卡片的呼吸灯效果</p></div><button className={animSettings.glowPulse ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, glowPulse: !c.glowPulse }))}><span /></button></div>
          <div className="setting-row"><div><h3>平滑滚动</h3><p>页面滚动的平滑过渡效果</p></div><button className={animSettings.smoothScroll ? "toggle on" : "toggle"} onClick={() => setAnimSettings(c => ({ ...c, smoothScroll: !c.smoothScroll }))}><span /></button></div>
          <button className="console-primary" style={{ marginTop: 16 }} onClick={saveAnim}><Sparkles size={17} /> 保存全部动画设置</button>
          {animMessage && <p className="save-message" style={{ marginTop: 12 }}>{animMessage}</p>}
        </div>
      </section>
    </div>
  );
}

/* ===================================================================
   APP ROOT
   =================================================================== */
function App() {
  const route = useRoute();
  const [siteSettings, setSiteSettings] = useState(readStoredSiteSettings);
  const [darkMode, setDarkMode] = useState(readDarkMode);
  const [auth, setAuth] = useState({ checking: true, user: null });

  const syncSettingsLocally = useCallback((nextSettings) => {
    const normalized = normalizeSiteSettings(nextSettings);
    setSiteSettings(normalized);
    try { window.localStorage.setItem(SITE_SETTINGS_STORAGE_KEY, JSON.stringify(normalized)); } catch { /* */ }
    return normalized;
  }, []);

  const handleUpdateSiteSettings = useCallback(async (nextSettings, options = {}) => {
    const normalized = syncSettingsLocally(nextSettings);
    if (options.remote) return normalized;
    const result = await api.saveSettings(normalized);
    syncSettingsLocally(result.settings);
    return result.settings;
  }, [syncSettingsLocally]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => { const next = !prev; saveDarkMode(next); return next; });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  useEffect(() => {
    if (route.page !== "console") { setAuth(current => current.checking ? { checking: false, user: null } : current); return; }
    let active = true;
    setAuth(current => ({ ...current, checking: true }));
    api.session().then(result => { if (active) setAuth({ checking: false, user: result.user }); }).catch(() => { if (active) setAuth({ checking: false, user: null }); });
    return () => { active = false; };
  }, [route.page]);

  const logout = async () => {
    try { await api.logout(); } finally { setAuth({ checking: false, user: null }); }
  };

  if (route.page === "console") {
    if (auth.checking) return <div className="boot-screen"><span className="boot-mark">⚡</span><strong>管理中心</strong><p>正在验证登录状态…</p></div>;
    if (!auth.user) return <AdminLogin onAuthenticated={user => setAuth({ checking: false, user })} />;
    return <ConsoleApp siteSettings={siteSettings} onUpdateSiteSettings={handleUpdateSiteSettings} currentUser={auth.user} onLogout={logout} />;
  }

  return <CorporateSite siteSettings={siteSettings} darkMode={darkMode} toggleDarkMode={toggleDarkMode} route={route} onSettingsSynced={syncSettingsLocally} />;
}

export default App;
